import type { FormEvent, ReactNode } from 'react'
import { useId, useState } from 'react'
import { cn } from '../../lib/cn'

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

export interface EmailCaptureFormProps {
  description: string
  placeholder: string
  submitLabel: string
  consentText: string
  termsContent: ReactNode
  invalidEmailError: string
  termsRequiredError: string
  successMessage: string
  onSubmit: (email: string, agreedToUpdates: boolean) => void
  validateEmail?: (email: string) => boolean
  className?: string
  descriptionClassName?: string
}

export const EmailCaptureForm = ({
  description,
  placeholder,
  submitLabel,
  consentText,
  termsContent,
  invalidEmailError,
  termsRequiredError,
  successMessage,
  onSubmit,
  validateEmail = isValidEmail,
  className,
  descriptionClassName,
}: EmailCaptureFormProps) => {
  const [email, setEmail] = useState('')
  const [agreedToUpdates, setAgreedToUpdates] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const consentLabelId = useId()

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setErrorMessage(null)

    if (!email.trim() || !validateEmail(email)) {
      setErrorMessage(invalidEmailError)
      return
    }

    if (!agreedToUpdates) {
      setErrorMessage(termsRequiredError)
      return
    }

    onSubmit(email, agreedToUpdates)
    setIsSubmitted(true)
  }

  if (isSubmitted) {
    return (
      <div
        className={cn(
          'flex w-full max-w-[400px] flex-col items-center gap-[30px] py-[50px]',
          className,
        )}
      >
        <p className='text-center text-[16px] leading-[1.3] text-white'>
          {successMessage}
        </p>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex w-full max-w-[400px] flex-col items-center gap-[30px] py-[50px]',
        className,
      )}
    >
      <p
        className={cn(
          'max-w-[251px] text-center text-[13px] leading-[1.2] text-white',
          descriptionClassName,
        )}
      >
        {description}
      </p>

      <form onSubmit={handleSubmit} className='flex w-full gap-[10px]'>
        <input
          type='email'
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={placeholder}
          className='h-[50px] flex-1 bg-white/20 px-4 text-[13px] max-md:text-[16px] text-white placeholder:text-white focus:outline-none'
          // aria-invalid must be omitted when the field is
          // valid — a static aria-invalid="false" fails
          // Aria_Attr_Implicit_Role_Is_Supported. Only render the attribute
          // once there's an active validation error.
          aria-invalid={errorMessage ? true : undefined}
        />
        <button
          type='submit'
          className='flex h-[50px] w-[50px] cursor-pointer items-center justify-center bg-white/20 text-white'
          aria-label={submitLabel}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 24 24'
            fill='none'
            aria-hidden='true'
            className='w-6 h-6'
          >
            <path
              fill='currentColor'
              fillRule='evenodd'
              d='M12.87 2.825a1.094 1.094 0 0 0-1.557 0 1.12 1.12 0 0 0 0 1.572l6.961 7.025H3.101c-.608 0-1.101.498-1.101 1.111 0 .614.493 1.112 1.1 1.112h15.108l-6.895 6.958a1.12 1.12 0 0 0 0 1.572c.43.433 1.127.433 1.557 0l8.808-8.89c.43-.433.43-1.137 0-1.57z'
              clipRule='evenodd'
            />
          </svg>
        </button>
      </form>

      <div className='flex w-full flex-col gap-[10px]'>
        <div className='flex w-full items-start gap-5'>
          <button
            type='button'
            role='checkbox'
            aria-checked={agreedToUpdates}
            aria-labelledby={consentLabelId}
            onClick={() => setAgreedToUpdates(!agreedToUpdates)}
            className='h-5 w-5 shrink-0 cursor-pointer bg-white/50'
          >
            {agreedToUpdates && (
              <svg
                width='20'
                height='20'
                viewBox='0 0 20 20'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                aria-hidden='true'
              >
                <path
                  d='M4 10L8 14L16 6'
                  className='stroke-edu-accent-indigo'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            )}
          </button>
          <div className='flex flex-col gap-[10px] text-[12px] leading-[1.2] text-white'>
            <p id={consentLabelId}>{consentText}</p>
            <p>{termsContent}</p>
          </div>
        </div>

        {errorMessage && (
          <p className='text-center text-[13px] text-text-error-1' role='alert'>
            {errorMessage}
          </p>
        )}
      </div>
    </div>
  )
}
